import React, { useCallback, useState } from "react";
import z from "zod";

import { ComponentProps } from "~/types/components";

import { useProduct } from "~/templates/product/useProduct";
import { useFunctionsGatsby } from "~/hooks/useFunctionsGatsby";
import { useKlaviyo } from "~/hooks/useKlaviyo";
import { useRichText } from "~/hooks/useRichText";

import { Sheet } from "~/components/Sheet";

import { Button, FormField, FormInput, FormTextarea } from "@bared/ui";

type ErrorMap = {
  [key: string]: string;
};

type AddNewQuestionProps = ComponentProps & {
  sku: string;
  productName: string;
  productHandle: string;
  closeReviewsSheet?: () => void;
};

const schema = z.object({
  email: z
    .string()
    .email("Email is invalid")
    .nonempty("This field is required"),
  name: z.string().nonempty("This field is required"),
  content: z.string().nonempty("This field is required"),
});

const AddNewQuestion = ({
  sku,
  productHandle,
  productName,
  closeReviewsSheet,
}: AddNewQuestionProps) => {
  const { productQuestionAdd, loading } = useFunctionsGatsby();
  const klaviyo = useKlaviyo();
  const { parseContent } = useRichText();
  const {
    template: { sheetFooterContent },
  } = useProduct();
  const footerContent = parseContent(sheetFooterContent);

  const [data, setData] = useState({
    name: "",
    email: "",
    content: "",
  });
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<ErrorMap>({});

  const handleChange = ({ target: { name, value } }) => {
    setData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = schema.safeParse(data);

    if (!result.success) {
      const mappedErrors = result.error.issues.reduce((map, issue) => {
        return {
          ...map,
          [issue.path[0] ?? "unknown"]: issue.message,
        };
      }, {});

      return setErrors(mappedErrors);
    }
    setErrors({});

    klaviyo.track("Review Ask", {
      email: data.email,
      grouping_hash: sku,
      name: data.name,
      question: data.content,
      url: `/products/${productHandle}`,
    });

    const { status } = await productQuestionAdd(
      data.email,
      sku,
      data.name,
      data.content,
      `/products/${productHandle}`,
    );
    setSuccess(status === "success");
  };
  const [isOpen, setIsOpen] = useState(false);

  const handleSheetToggle = useCallback(() => {
    setIsOpen((prev) => {
      const nextState = !prev;

      // Close the reviews sheet when opening the question sheet
      if (nextState && closeReviewsSheet) {
        closeReviewsSheet();
      }

      return nextState;
    });
  }, [closeReviewsSheet]);

  return (
    <>
      <Button
        size="large"
        variant="secondary"
        onClick={handleSheetToggle}
        className="w-full md:order-last md:max-w-[190px]"
      >
        Ask a Question
      </Button>

      <Sheet
        className="px-mini py-midi md:px-maxi"
        isOpen={isOpen}
        onClose={handleSheetToggle}
        title="Ask a Question"
      >
        {!success ? (
          <>
            <h3 className="font-display text-h3 leading-h3 lg:text-[32px]">
              Have a question about {productName}?
            </h3>

            <form className="my-midi flex flex-col space-y-mini">
              <div className="grid grid-cols-1 gap-mini md:grid-cols-2">
                <FormField
                  name="questions-name"
                  label="First Name"
                  required
                  errors={errors["name"] ? [errors["name"]] : undefined}
                >
                  <FormInput
                    name="name"
                    value={data.name}
                    onChange={handleChange}
                  />
                </FormField>

                <FormField
                  name="questions-email"
                  label="Your Email"
                  errors={errors["email"] ? [errors["email"]] : undefined}
                  required
                >
                  <FormInput
                    name="email"
                    value={data.email}
                    onChange={handleChange}
                  />
                </FormField>
              </div>

              <FormField
                name="question"
                label="Your Question"
                errors={errors["content"] ? [errors["content"]] : undefined}
                required
              >
                <FormTextarea
                  name="content"
                  value={data.content}
                  onChange={handleChange}
                />
              </FormField>
            </form>

            <div className="flex w-full justify-end">
              <Button
                variant="primary"
                size="large"
                onClick={(e) => handleSubmit(e)}
                className="float-right w-full md:w-auto"
                disabled={loading}
              >
                {loading ? "Submitting" : "Submit Question"}
              </Button>
            </div>

            {footerContent && (
              <div className="mt-maxi border-t border-primary-300 pt-midi">
                {footerContent}
              </div>
            )}
          </>
        ) : (
          <div>
            <h2 className="mb-mini text-center font-display text-h2 md:text-[44px]">
              Thanks for your question!
            </h2>
            <p className="mx-auto mb-mini text-center leading-caption">
              We love hearing from you. Our team will publish it shortly.
            </p>
          </div>
        )}
      </Sheet>
    </>
  );
};

export default AddNewQuestion;
